// Import react
import React, { useState, useEffect } from "react";

// Import states, contexts, and CSS
import { useActiveUser } from "../../store/activeUserContext";
import { SpinnerGeneric } from "../../utils/general/spinners";
import { Card, Form, Row, Col, Button, Table } from "react-bootstrap";
import { PlusCircle, XCircle, ArrowClockwise } from "react-bootstrap-icons";
import Modal from "react-bootstrap/Modal";
import Pagination from "react-bootstrap/Pagination";

// Importa ExcelJS
import ExcelJS from "exceljs";

// Import components
import ItemListRow from "./itemListRow";
import ItemListFilterComponent from "./itemListFilter";

const ItemListComponent = () => {
  const { activeUser } = useActiveUser();
  const userId = activeUser._id;
  const token = localStorage.getItem("token");

  const [columnsToShow, setColumnsToShow] = useState([]);

  const [totalColumns, setTotalColumns] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [newItem, setNewItem] = useState({
    itemCode: "",
    itemType: "",
    itemName: "",
    itemDescription: "",
    itemCategory1: "",
    itemCategory2: "",
    itemCategory3: "",
    itemCategory4: "",
    itemCategory5: "",
    itemSize: "",
    itemBrand: "",
    itemWeight: "",
  });

  // Import file
  const [importFile, setImportFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now()); // Stato per la key dell'input file
  // Modal
  const [modalShow, setModalShow] = useState(false); // Modal visibility state
  const handleCloseModal = () => {
    setModalShow(false);
    setImportFile(null);
  }; // Function to close the Modal

  // Filter, pages and sorting
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10); // puoi anche renderlo selezionabile
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sort, setSort] = useState("itemName");

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const maxSize = 3 * 1024 * 1024; // 5MB in byte

    if (file && file.size > maxSize) {
      alert("Il file è troppo grande. Massimo 3MB.");
      event.target.value = ""; // Resetta il campo file
      return;
    }

    // Altrimenti puoi procedere a inviare il file
    setImportFile(file);
  };

  const handleFileReset = () => {
    setImportFile(null); // Resetta lo stato del file
    setFileInputKey(Date.now()); // Cambia la key per forzare un re-render dell'input
  };

  const handleImportFile = async () => {
    setIsLoading(true);
    if (!importFile) return;

    const formData = new FormData();
    formData.append("file", importFile);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/item/users/${activeUser._id}/item/import`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      const result = await response.json(); // 👈 Leggiamo SEMPRE la risposta

      if (response.ok) {
        if (result.invalidItems && result.invalidItems.length > 0) {
          const invalidList = result.invalidItems
            .map((item) => `• ${item.itemCode} — ${item.errors.join(", ")}`)
            .join("\n");

          alert(`${result.message}\n\nArticoli non importati:\n${invalidList}`);
        } else {
          alert(result.message || "Importazione completata!");
        }

        setUpdate((prev) => !prev);
        setImportFile(null);
        handleFileReset();
      } else {
        console.error("Errore durante importazione:", result.message);
        alert(result.message || "Errore nell'importazione.");
      }
    } catch (error) {
      console.error("Errore import:", error);
      alert("Errore di connessione al server durante l'importazione.");
    } finally {
      handleCloseModal();
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setNewItem({ ...newItem, [e.target.name]: e.target.value });
  };

  const handleAddItem = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/item/users/${userId}/item`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(newItem),
        }
      );

      const data = await response.json(); // 👈 Leggiamo SEMPRE la risposta

      if (response.ok) {
        setItemList([...itemList, data]);
        setNewItem({ itemCode: "", itemType: "", itemName: "" });
        alert(data.message || "Articolo aggiunto con successo!");
      } else {
        const errorMsg = data.errors
          ? `Errore:\n- ${data.errors.join("\n- ")}`
          : data.message || "Errore durante l'aggiunta dell'articolo.";
        alert(errorMsg);
      }
    } catch (error) {
      console.error("Errore durante la richiesta POST:", error);
      alert("Errore durante la connessione al server.");
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  // Funzione per scaricare il modello Excel
  const downloadTemplate = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Item Template");

    // Aggiungi le intestazioni delle colonne
    worksheet.columns = [
      { header: "Item Code(mandatory)*", key: "itemCode", width: 30 },
      { header: "Item Name", key: "itemName", width: 30 },
      { header: "Item Type", key: "itemType", width: 30 },
      { header: "Item Description", key: "itemDescription", width: 30 },
      { header: "Item Category 1", key: "itemCategory1", width: 30 },
      { header: "Item Category 2", key: "itemCategory2", width: 30 },
      { header: "Item Category 3", key: "itemCategory3", width: 30 },
      { header: "Item Category 4", key: "itemCategory4", width: 30 },
      { header: "Item Category 5", key: "itemCategory5", width: 30 },
      { header: "Item Size", key: "itemSize", width: 30 },
      { header: "Item Brand", key: "itemBrand", width: 30 },
      { header: "Item Weight", key: "itemWeight", width: 30 },
    ];

    // Aggiungi una riga vuota di esempio (opzionale)
    worksheet.addRow({ itemCode: "" });

    // Impostazione per scaricare il file
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "item_template.xlsx";
      a.click();
      URL.revokeObjectURL(url); // Libera la memoria dopo il download
    });
  };

  useEffect(() => {
    setIsLoading(true);

    const timer = setTimeout(() => {
      const fetchItems = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/api/item/users/${
              activeUser._id
            }/items?filter=${encodeURIComponent(
              filter
            )}&page=${page}&limit=${limit}&sort=${sort}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const data = await response.json();

          if (response.ok) {
            setItemList(data.items);
            setTotalItems(data.totalItems);
            setTotalPages(data.totalPages);
          } else {
            console.error("Errore dal server:", data.message);
          }
        } catch (error) {
          console.error("Errore fetch:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchItems();
    }, 500); // meno delay

    return () => clearTimeout(timer);
  }, [filter, page, update, limit, sort, activeUser]);

  return (
    <div>
      {/* Item Table */}
      <Card className="mb-5 shadow-sm">
        <Card.Header>
          <Card.Title className="mb-0">Item Table</Card.Title>
        </Card.Header>
        <Card.Body>
          <ItemListFilterComponent
            filter={filter}
            setFilter={setFilter}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            totalPages={totalPages}
            sort={sort}
            setSort={setSort}
            setUpdate={setUpdate}
            isLoading={isLoading}
            itemList={itemList}
            setModalShow={setModalShow}
            columnsToShow={columnsToShow}
            setColumnsToShow={setColumnsToShow}
            totalColumns={totalColumns}
            setTotalColumns={setTotalColumns}
          />

          {/* Item List */}
          <div style={{ overflowX: "auto", overflowY: "auto" }}>
            <Table bordered hover responsive>
              <thead className="table-light">
                <tr>
                  <th>Item ID</th>
                  <th>Item Code</th>
                  {columnsToShow.map((column, index) => (
                    <th>{column.headerName}</th>
                  ))}

                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="15" className="text-center">
                      <SpinnerGeneric />
                    </td>
                  </tr>
                ) : itemList.length === 0 ? (
                  <tr>
                    <td colSpan="15" className="text-center">
                      Nessun articolo trovato
                    </td>
                  </tr>
                ) : (
                  itemList.map((item, index) => (
                    <ItemListRow
                      key={index}
                      index={index}
                      item={item}
                      setUpdate={setUpdate}
                      setFilter={setFilter}
                      columnsToShow={columnsToShow}
                    />
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={modalShow} // Bind the Modal visibility to modalShow
        onHide={handleCloseModal} // Close modal when user clicks close button
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
<Modal.Header closeButton={!isLoading}>
<Modal.Title id="contained-modal-title-vcenter">
            Aggiungi articoli
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Import from Excel/CSV */}
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <Card.Title className="mb-0">Import Item</Card.Title>
            </Card.Header>

            {isLoading ? (
              <Card.Body>
                <SpinnerGeneric />
              </Card.Body>
            ) : (
              <Card.Body>
                <Form.Group controlId="fileUpload" className="mb-3">
                  <Form.Label>Select Excel or CSV file</Form.Label>
                  <Form.Control
                    key={fileInputKey}
                    type="file"
                    accept=".csv, .xlsx"
                    onChange={handleFileChange}
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleImportFile}
                  disabled={isLoading || !importFile}
                >
                  <PlusCircle className="me-2" size={16} />
                  Import File
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  className="ms-3"
                  onClick={downloadTemplate}
                >
                  <XCircle className="me-2" size={16} />
                  Download Template
                </Button>

                {importFile && (
                  <Button
                    variant="danger"
                    size="sm"
                    className="ms-3"
                    onClick={handleFileReset}
                  >
                    Cancel file
                  </Button>
                )}
              </Card.Body>
            )}
          </Card>

          {/* Add New Item */}
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <Card.Title className="mb-0">Add New Item</Card.Title>
            </Card.Header>

            {isLoading ? (
              <Card.Body>
                <SpinnerGeneric />
              </Card.Body>
            ) : (
              <Card.Body>
                <Form>
                  <Row className="align-items-center mb-3">
                    <Col md={2}>
                      <Form.Label>Item Code *</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={50}
                        type="text"
                        placeholder="Enter item code"
                        name="itemCode"
                        value={newItem.itemCode}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {50 - (newItem?.itemCode?.length || 0)} characters left
                      </Form.Text>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Category 1</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={50}
                        type="text"
                        placeholder="Enter item category 1"
                        name="itemCategory1"
                        value={newItem.itemCategory1}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {50 - (newItem?.itemCategory1?.length || 0)} characters
                        left
                      </Form.Text>
                    </Col>
                  </Row>
                  <Row className="align-items-center mb-3">
                    <Col md={2}>
                      <Form.Label>Item Type</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={50}
                        type="text"
                        placeholder="Enter item type"
                        name="itemType"
                        value={newItem.itemType}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {50 - (newItem?.itemType?.length || 0)} characters left
                      </Form.Text>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Category 2</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={50}
                        type="text"
                        placeholder="Enter item category 2"
                        name="itemCategory2"
                        value={newItem.itemCategory2}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {50 - (newItem?.itemCategory2?.length || 0)} characters
                        left
                      </Form.Text>
                    </Col>
                  </Row>

                  <Row className="align-items-center mb-3">
                    <Col md={2}>
                      <Form.Label>Item Name</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={100}
                        type="text"
                        placeholder="Enter item name"
                        name="itemName"
                        value={newItem.itemName}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {100 - (newItem?.itemName?.length || 0)} characters left
                      </Form.Text>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Category 3</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={50}
                        type="text"
                        placeholder="Enter item category 3"
                        name="itemCategory3"
                        value={newItem.itemCategory3}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {50 - (newItem?.itemCategory3?.length || 0)} characters
                        left
                      </Form.Text>
                    </Col>
                  </Row>
                  <Row className="align-items-center mb-3">
                    <Col md={2}>
                      <Form.Label>Item Description</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={300}
                        type="text"
                        placeholder="Enter item description"
                        name="itemDescription"
                        value={newItem.itemDescription}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {300 - (newItem?.itemDescription?.length || 0)}{" "}
                        characters left
                      </Form.Text>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Category 4</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={50}
                        type="text"
                        placeholder="Enter item category 4"
                        name="itemCategory4"
                        value={newItem.itemCategory4}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {50 - (newItem?.itemCategory4?.length || 0)} characters
                        left
                      </Form.Text>
                    </Col>
                  </Row>
                  <Row className="align-items-center mb-3">
                    <Col md={2}>
                      <Form.Label>Item Size</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={50}
                        type="text"
                        placeholder="Enter item size"
                        name="itemSize"
                        value={newItem.itemSize}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {50 - (newItem?.itemSize?.length || 0)} characters left
                      </Form.Text>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Category 5</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={50}
                        type="text"
                        placeholder="Enter item category 5"
                        name="itemCategory5"
                        value={newItem.itemCategory5}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {50 - (newItem?.itemCategory5?.length || 0)} characters
                        left
                      </Form.Text>
                    </Col>
                  </Row>
                  <Row className="align-items-center mb-3">
                    <Col md={2}>
                      <Form.Label>Item Weight</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={20}
                        type="text"
                        placeholder="Enter item weight"
                        name="itemWeight"
                        value={newItem.itemWeight}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {20 - (newItem?.itemWeight?.length || 0)} characters
                        left
                      </Form.Text>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Brand</Form.Label>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        maxLength={50}
                        type="text"
                        placeholder="Enter item brand"
                        name="itemBrand"
                        value={newItem.itemBrand}
                        onChange={handleChange}
                      />
                      <Form.Text muted>
                        {50 - (newItem?.itemBrand?.length || 0)} characters left
                      </Form.Text>
                    </Col>
                  </Row>

                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleAddItem}
                    disabled={newItem?.itemCode === ""}
                  >
                    <PlusCircle className="me-2" size={16} />
                    Add Item
                  </Button>
                </Form>
              </Card.Body>
            )}
          </Card>
        </Modal.Body>
        <Modal.Footer>
          {isLoading ? (
            <></>
          ) : (
            <>
              <Button variant="secondary" onClick={handleCloseModal}>
                Close
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ItemListComponent;
